// ════════════════════════════════════════════════════════════════════
// ILLEGAL EMPIRE — AchievementGallery
// ────────────────────────────────────────────────────────────────────
// The player-facing wall of achievements. Grimoire-framed. Grouped by
// origin game, earned items lit and dated, locked items dimmed (hidden
// ones masked as "???"). A header shows total renown + completion. A
// "Cross-Game Rewards" rail surfaces the gates — what an achievement in
// one IE game unlocks in another — so the universe's interconnection is
// VISIBLE, not just plumbing.
//
// Reads everything from the hook — single source of truth, live-updating.
//
// ── USAGE ────────────────────────────────────────────────────────────
//   <AchievementGallery uid={uid} onClose={() => goBack()} />
// ════════════════════════════════════════════════════════════════════

import React, { useMemo } from "react";
import { GM, MANUSCRIPT_BG } from "./theme";
import { useAchievements } from "./useAchievements";
import {
  ACHIEVEMENTS,
  GAME_NAMESPACES,
  REWARD_GATES,
  ACHIEVEMENT_RARITY_META,
  getAchievementDisplay,
  missingForReward,
  allAchievementIds,
} from "./achievements";

// Order games appear in the gallery (empire meta last — it's the capstone).
const GAME_ORDER = ["terror", "pier", "casino", "sol", "empire"];

export default function AchievementGallery({ uid, onClose }) {
  const { map, count, renown } = useAchievements(uid);

  const total = useMemo(() => allAchievementIds().length, []);
  const pct = total ? Math.round((count / total) * 100) : 0;

  // Bucket every registry achievement by its origin game, preserving order.
  const grouped = useMemo(() => {
    const buckets = {};
    for (const g of GAME_ORDER) buckets[g] = [];
    for (const id of allAchievementIds()) {
      const def = ACHIEVEMENTS[id];
      (buckets[def.game] || (buckets[def.game] = [])).push(id);
    }
    // within each game: earned first, then by rarity (rarest first)
    for (const g of Object.keys(buckets)) {
      buckets[g].sort((a, b) => {
        const ea = !!map[a], eb = !!map[b];
        if (ea !== eb) return ea ? -1 : 1;
        const ra = ACHIEVEMENT_RARITY_META[ACHIEVEMENTS[a].rarity]?.order ?? 0;
        const rb = ACHIEVEMENT_RARITY_META[ACHIEVEMENTS[b].rarity]?.order ?? 0;
        return rb - ra;
      });
    }
    return buckets;
  }, [map]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 800,
        overflowY: "auto",
        background: MANUSCRIPT_BG,
        fontFamily: GM.font,
        color: GM.vellum,
        padding: "24px 16px 64px",
      }}
    >
      <div style={{ maxWidth: 880, margin: "0 auto" }}>
        {/* ── Header ─────────────────────────────────────────── */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 16,
            borderBottom: `1px solid ${GM.pageBorder}`,
            paddingBottom: 14,
            marginBottom: 22,
          }}
        >
          <div>
            <div
              style={{
                fontFamily: GM.fontD,
                fontSize: 28,
                fontWeight: 700,
                color: GM.inkGoldBright,
                letterSpacing: "0.04em",
              }}
            >
              ✦ Deeds & Renown
            </div>
            <div style={{ fontSize: 12, color: GM.vellumDim, marginTop: 4 }}>
              {count} of {total} earned · {pct}% · {renown} renown
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              style={ghostBtn}
              onMouseEnter={(e) => (e.currentTarget.style.color = GM.inkGoldBright)}
              onMouseLeave={(e) => (e.currentTarget.style.color = GM.vellumDim)}
            >
              ← Back
            </button>
          )}
        </div>

        {/* completion bar */}
        <div
          style={{
            height: 6,
            borderRadius: 4,
            background: GM.pageMid,
            border: `1px solid ${GM.pageBorder}`,
            overflow: "hidden",
            marginBottom: 28,
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: "100%",
              background: `linear-gradient(90deg, ${GM.inkGoldDim}, ${GM.inkGoldBright})`,
              transition: "width 600ms ease",
            }}
          />
        </div>

        {/* ── Cross-Game Rewards rail ────────────────────────── */}
        <CrossGameRewards map={map} />

        {/* ── Per-game sections ──────────────────────────────── */}
        {GAME_ORDER.map((g) => {
          const ids = grouped[g];
          if (!ids || ids.length === 0) return null;
          const ns = GAME_NAMESPACES[g];
          const earnedHere = ids.filter((id) => map[id]).length;
          return (
            <section key={g} style={{ marginBottom: 30 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: ns.tint,
                    boxShadow: `0 0 8px ${ns.tint}aa`,
                  }}
                />
                <h3
                  style={{
                    fontFamily: GM.fontD,
                    fontSize: 17,
                    margin: 0,
                    color: GM.vellum,
                    letterSpacing: "0.05em",
                  }}
                >
                  {ns.label}
                </h3>
                <span style={{ fontSize: 11, color: GM.vellumFaint }}>
                  {earnedHere}/{ids.length}
                </span>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                  gap: 12,
                }}
              >
                {ids.map((id) => (
                  <AchievementCard
                    key={id}
                    id={id}
                    record={map[id]}
                    tint={ns.tint}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

// ── Single achievement card ──────────────────────────────────────────
function AchievementCard({ id, record, tint }) {
  const def = ACHIEVEMENTS[id];
  const earned = !!record;
  const display = getAchievementDisplay(id, earned);
  const rarity = ACHIEVEMENT_RARITY_META[def.rarity] || ACHIEVEMENT_RARITY_META.common;

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        gap: 12,
        padding: "12px 14px",
        borderRadius: 9,
        background: earned
          ? `linear-gradient(180deg, ${GM.pageMid}, ${GM.pageWarm})`
          : GM.page,
        border: `1px solid ${earned ? GM.inkGoldDim : GM.pageBorder}`,
        boxShadow: earned ? `0 0 12px ${tint}22, inset 0 1px 0 ${GM.inkGold}1f` : "none",
        opacity: earned ? 1 : 0.62,
      }}
    >
      <div
        style={{
          flex: "0 0 auto",
          width: 40,
          height: 40,
          display: "grid",
          placeItems: "center",
          borderRadius: "50%",
          fontSize: 20,
          background: earned
            ? `radial-gradient(circle at 35% 30%, ${tint}aa, ${tint}33 65%, ${GM.page})`
            : GM.pageMid,
          border: `1.5px solid ${earned ? rarity.tint : GM.pageBorder}`,
          filter: earned ? "none" : "grayscale(1)",
        }}
      >
        {earned ? display.icon : "🔒"}
      </div>

      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginBottom: 2,
          }}
        >
          <span
            style={{
              fontSize: 8,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              fontWeight: 700,
              color: rarity.tint,
            }}
          >
            {rarity.label}
          </span>
          {def.points ? (
            <span style={{ fontSize: 8, color: GM.vellumFaint }}>
              · {def.points}
            </span>
          ) : null}
        </div>
        <div
          style={{
            fontFamily: GM.fontD,
            fontSize: 14,
            color: earned ? GM.vellum : GM.vellumFaint,
            fontWeight: 700,
            lineHeight: 1.15,
          }}
        >
          {display.name}
        </div>
        <div
          style={{
            fontSize: 11,
            color: GM.vellumDim,
            marginTop: 3,
            lineHeight: 1.3,
          }}
        >
          {display.desc}
        </div>
        {earned && record?.earnedAt ? (
          <div style={{ fontSize: 9, color: GM.vellumFaint, marginTop: 5 }}>
            Earned {new Date(record.earnedAt).toLocaleDateString()}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ── Cross-Game Rewards rail ───────────────────────────────────────────
// Surfaces the REWARD_GATES so players SEE the interconnection: which
// achievement (often from another game) unlocks which reward. Hidden gates
// (e.g. the Unwritten) are omitted — discovery only.
function CrossGameRewards({ map }) {
  const gates = useMemo(
    () =>
      Object.entries(REWARD_GATES).filter(([, gate]) => {
        // omit gates that depend solely on hidden achievements
        const reqs = gate.requires || gate.anyOf || [];
        return !reqs.every((id) => ACHIEVEMENTS[id]?.hidden);
      }),
    []
  );

  if (gates.length === 0) return null;

  return (
    <section style={{ marginBottom: 30 }}>
      <h3
        style={{
          fontFamily: GM.fontD,
          fontSize: 17,
          margin: "0 0 4px",
          color: GM.inkGoldBright,
          letterSpacing: "0.05em",
        }}
      >
        ⛓ Cross-Game Rewards
      </h3>
      <div style={{ fontSize: 11, color: GM.vellumDim, marginBottom: 12 }}>
        Deeds in one corner of the Empire open doors in another.
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gap: 12,
        }}
      >
        {gates.map(([rewardId, gate]) => {
          const missing = missingForReward(map, rewardId);
          const unlocked = missing.length === 0;
          const reqs = gate.requires || gate.anyOf || [];
          return (
            <div
              key={rewardId}
              style={{
                padding: "12px 14px",
                borderRadius: 9,
                background: unlocked
                  ? `linear-gradient(180deg, ${GM.pageMid}, ${GM.pageWarm})`
                  : GM.page,
                border: `1px solid ${unlocked ? GM.inkGold : GM.pageBorder}`,
                boxShadow: unlocked
                  ? `0 0 12px ${GM.inkGold}33, inset 0 1px 0 ${GM.inkGold}22`
                  : "none",
                opacity: unlocked ? 1 : 0.78,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                }}
              >
                <span
                  style={{
                    fontSize: 8,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: GM.vellumFaint,
                    fontWeight: 700,
                  }}
                >
                  {gate.type}
                </span>
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: unlocked ? GM.inkGoldBright : GM.vellumFaint,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                  }}
                >
                  {unlocked ? "✦ Unlocked" : "🔒 Locked"}
                </span>
              </div>
              <div
                style={{
                  fontFamily: GM.fontD,
                  fontSize: 14,
                  fontWeight: 700,
                  color: GM.vellum,
                  margin: "4px 0 6px",
                }}
              >
                {prettyRewardId(rewardId)}
              </div>
              <div style={{ fontSize: 11, color: GM.vellumDim, lineHeight: 1.35 }}>
                {gate.note}
              </div>
              {!unlocked && (
                <div style={{ fontSize: 10, color: GM.vellumFaint, marginTop: 8 }}>
                  {gate.anyOf ? "Needs any of: " : "Needs: "}
                  {missing
                    .map((id) => ACHIEVEMENTS[id]?.name || id)
                    .join(", ")}
                </div>
              )}
              {unlocked && reqs.length > 0 && (
                <div style={{ fontSize: 10, color: GM.vellumFaint, marginTop: 8 }}>
                  Earned via:{" "}
                  {reqs.map((id) => ACHIEVEMENTS[id]?.name || id).join(", ")}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// "radiant:jynxi_green" → "J'ynxi Green (Radiant)"
function prettyRewardId(rewardId) {
  const [type, key] = rewardId.split(":");
  const words = (key || "")
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
  const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);
  return `${words} (${typeLabel})`;
}

const ghostBtn = {
  background: "transparent",
  border: "none",
  color: GM.vellumDim,
  fontFamily: GM.font,
  fontSize: 13,
  letterSpacing: "0.06em",
  cursor: "pointer",
  padding: "6px 4px",
  transition: "color 160ms ease",
};
